export interface Vec2 {
	x: number;
	y: number;
}

export interface Body {
	pos: Vec2;
	vel: Vec2;
	mass: number;
	radius: number;
}

export interface SimState {
	craft: Body;
	planet: Body;
	trail: Vec2[];
	predictedPath: Vec2[];
	time: number;
	status: "aiming" | "flying" | "escaped" | "crashed";
	initialSpeed: number;
	exitSpeed: number;
	speedGain: number;
}

const G = 800; // gravitational constant (tuned for visual appeal)
const MAX_TRAIL = 2000;
let ESCAPE_DISTANCE = 1200;

export function setEscapeDistance(d: number) {
	ESCAPE_DISTANCE = d;
}

export function getEscapeDistance() {
	return ESCAPE_DISTANCE;
}
const PREDICT_STEPS = 600;
const PREDICT_DT = 0.016;

export function vec2(x: number, y: number): Vec2 {
	return { x, y };
}

export function add(a: Vec2, b: Vec2): Vec2 {
	return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec2, b: Vec2): Vec2 {
	return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(v: Vec2, s: number): Vec2 {
	return { x: v.x * s, y: v.y * s };
}

export function length(v: Vec2): number {
	return Math.sqrt(v.x * v.x + v.y * v.y);
}

export function normalize(v: Vec2): Vec2 {
	const len = length(v);
	if (len === 0) return { x: 0, y: 0 };
	return { x: v.x / len, y: v.y / len };
}

export function dist(a: Vec2, b: Vec2): number {
	return length(sub(a, b));
}

function gravitationalAccel(craftPos: Vec2, planetPos: Vec2, planetMass: number): Vec2 {
	const diff = sub(planetPos, craftPos);
	const d = length(diff);
	if (d < 1) return vec2(0, 0);
	const forceMag = (G * planetMass) / (d * d);
	const dir = normalize(diff);
	return scale(dir, forceMag);
}

export function createInitialState(
	canvasWidth: number,
	canvasHeight: number,
	planetOrbitRadius: number,
	planetMass: number,
	planetSpeed: number,
): SimState {
	const cx = canvasWidth / 2;
	const cy = canvasHeight / 2;

	return {
		craft: {
			pos: vec2(cx - canvasWidth * 0.35, cy + canvasHeight * 0.2),
			vel: vec2(0, 0),
			mass: 1,
			radius: 4,
		},
		planet: {
			pos: vec2(cx + planetOrbitRadius, cy),
			vel: vec2(0, -planetSpeed),
			mass: planetMass,
			radius: 28,
		},
		trail: [],
		predictedPath: [],
		time: 0,
		status: "aiming",
		initialSpeed: 0,
		exitSpeed: 0,
		speedGain: 0,
	};
}

export function updatePlanetOrbit(
	planet: Body,
	centerX: number,
	centerY: number,
	orbitRadius: number,
	planetSpeed: number,
	time: number,
): void {
	const angle = (-planetSpeed / orbitRadius) * time;
	planet.pos.x = centerX + Math.cos(angle) * orbitRadius;
	planet.pos.y = centerY + Math.sin(angle) * orbitRadius;
	// tangential velocity
	planet.vel.x = -Math.sin(angle) * planetSpeed;
	planet.vel.y = Math.cos(angle) * planetSpeed;
}

export function stepSimulation(
	state: SimState,
	dt: number,
	centerX: number,
	centerY: number,
	orbitRadius: number,
	planetSpeed: number,
): void {
	if (state.status !== "flying") return;

	const substeps = 4;
	const subDt = dt / substeps;

	for (let s = 0; s < substeps; s++) {
		state.time += subDt;
		updatePlanetOrbit(state.planet, centerX, centerY, orbitRadius, planetSpeed, state.time);

		// Velocity Verlet integration
		const accel1 = gravitationalAccel(state.craft.pos, state.planet.pos, state.planet.mass);
		state.craft.pos = add(state.craft.pos, add(scale(state.craft.vel, subDt), scale(accel1, 0.5 * subDt * subDt)));
		const accel2 = gravitationalAccel(state.craft.pos, state.planet.pos, state.planet.mass);
		state.craft.vel = add(state.craft.vel, scale(add(accel1, accel2), 0.5 * subDt));

		// Trail
		state.trail.push({ ...state.craft.pos });
		if (state.trail.length > MAX_TRAIL) {
			state.trail.shift();
		}

		// Collision check
		const d = dist(state.craft.pos, state.planet.pos);
		if (d < state.planet.radius + state.craft.radius) {
			state.status = "crashed";
			return;
		}

		// Escape check
		const distFromCenter = dist(state.craft.pos, vec2(centerX, centerY));
		if (distFromCenter > ESCAPE_DISTANCE) {
			state.exitSpeed = length(state.craft.vel);
			state.speedGain = state.exitSpeed - state.initialSpeed;
			state.status = "escaped";
			return;
		}
	}
}

export function computePredictedPath(
	craftPos: Vec2,
	craftVel: Vec2,
	planet: Body,
	centerX: number,
	centerY: number,
	orbitRadius: number,
	planetSpeed: number,
	startTime: number,
): Vec2[] {
	const path: Vec2[] = [];
	let pos = { ...craftPos };
	let vel = { ...craftVel };
	let t = startTime;
	const tempPlanet = { ...planet, pos: { ...planet.pos }, vel: { ...planet.vel } };

	for (let i = 0; i < PREDICT_STEPS; i++) {
		t += PREDICT_DT;
		updatePlanetOrbit(tempPlanet, centerX, centerY, orbitRadius, planetSpeed, t);

		const accel1 = gravitationalAccel(pos, tempPlanet.pos, tempPlanet.mass);
		pos = add(pos, add(scale(vel, PREDICT_DT), scale(accel1, 0.5 * PREDICT_DT * PREDICT_DT)));
		const accel2 = gravitationalAccel(pos, tempPlanet.pos, tempPlanet.mass);
		vel = add(vel, scale(add(accel1, accel2), 0.5 * PREDICT_DT));

		path.push({ ...pos });

		const d = dist(pos, tempPlanet.pos);
		if (d < tempPlanet.radius + 4) break;

		const distFromCenter = dist(pos, vec2(centerX, centerY));
		if (distFromCenter > ESCAPE_DISTANCE) break;
	}

	return path;
}
